import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import {
  AltitudeBand,
  Vertex,
  altitudeBand,
  constrainsPlanning,
  geofenceVertices,
  isInsideBoundary,
  isWithinBand,
} from '../utils/geofence';

export interface GeofenceViolations {
  /** True when a fence is loaded that actually constrains this draft. */
  enforced: boolean;
  /** Boundary vertices to draw, empty when nothing constrains the draft. */
  vertices: Vertex[];
  /** Ids of waypoints outside the boundary. */
  outsideWaypointIds: Set<string>;
  /** True when a home base is set and falls outside the boundary. */
  homeBaseOutside: boolean;
  /**
   * Note there is no total. There used to be a `count` of offending positions,
   * and #541 gave it a second kind of offence to hold: a height is not a
   * position, and one number covering both let a caller say "3 positions
   * outside" about a legal route flown too high. Callers add up the parts they
   * are actually talking about.
   */
  /**
   * The heights this draft may be flown at (issue #541). Always present: the
   * ceiling is regulatory, so it applies with no fence drawn and no drone
   * chosen.
   */
  band: AltitudeBand;
  /**
   * Descriptions of the profile heights the band does not admit, in the order
   * the operator would read them. Empty when the profile is legal.
   */
  outsideAltitudes: string[];
}

/**
 * Which parts of the current draft fall outside the selected drone's inclusion
 * geofence (issue #451).
 *
 * Computed in one place and shared, so the map outline, the marker badges and
 * the panel summary can never disagree with each other — and so the whole route
 * is measured once per change rather than once per marker.
 *
 * The home base is included because the backend rejects on it too: the mission
 * returns there, so a home base outside the fence is a crossing.
 *
 * The altitude band (#541) is deliberately *not* gated on a fence being loaded.
 * The ceiling comes from the law rather than from anything the operator drew,
 * so a draft with no drone chosen is still checked against it.
 */
export const useGeofenceViolations = (): GeofenceViolations => {
  const geofence = useSelector((state: RootState) => state.geofence.geofence);
  const droneId = useSelector((state: RootState) => state.missionPlanner.droneId);
  const waypoints = useSelector(
    (state: RootState) => state.missionPlanner.draft.waypoints
  );
  const homeBase = useSelector(
    (state: RootState) => state.missionPlanner.draft.homeBase
  );
  const profile = useSelector(
    (state: RootState) => state.missionPlanner.draft.profile
  );

  return useMemo(() => {
    const fenced = droneId !== null && constrainsPlanning(geofence);
    const band = altitudeBand(fenced ? geofence : null);

    // The heights the mission would actually be flown at: the profile's
    // default, plus every control point on its altitude curve. The curve is
    // where a route climbs, so a check that read only the default would clear
    // the very missions that leave the band.
    const outsideAltitudes: string[] = [];
    if (!isWithinBand(band, profile.altitudeM)) {
      outsideAltitudes.push(`Cruise altitude ${profile.altitudeM} m`);
    }
    for (const point of profile.altitudeCurve.points) {
      if (!isWithinBand(band, point.value)) {
        outsideAltitudes.push(`${point.value} m at ${point.distanceKm} km`);
      }
    }

    const none: GeofenceViolations = {
      enforced: false,
      vertices: [],
      outsideWaypointIds: new Set<string>(),
      homeBaseOutside: false,
      band,
      outsideAltitudes,
    };

    // No drone chosen means no boundary is enforced, whatever happens to be
    // loaded — but the ceiling above still is.
    if (!fenced) {
      return none;
    }
    const vertices = geofenceVertices(geofence!);
    if (vertices.length === 0) {
      // Unusable geometry: nothing can be drawn and nothing can be judged. The
      // server will refuse the save, so claiming everything is fine would lie.
      return none;
    }

    const outsideWaypointIds = new Set<string>();
    for (const wp of waypoints) {
      if (!isInsideBoundary(vertices, wp.lat, wp.lng)) {
        outsideWaypointIds.add(wp.id);
      }
    }
    const homeBaseOutside =
      homeBase !== null && !isInsideBoundary(vertices, homeBase.lat, homeBase.lng);

    return {
      enforced: true,
      vertices,
      outsideWaypointIds,
      homeBaseOutside,
      band,
      outsideAltitudes,
    };
  }, [droneId, geofence, waypoints, homeBase, profile]);
};
